// docs/112 UP8 — a wonder's gift of enchantment waiting on its hero's player: who offered it, what it
// does, the hero's pieces it can go on (worn or packed), and "Lay it on" the selected one, for free.
// Shown at the top of the inventory while the hero is home; the module (`accept_upgrade_gift`) judges.
import { useMemo, useState, type CSSProperties } from 'react';
import { Button, confirmDialog } from '../components';
import { useUpgradeGifts } from '../../core/subscriptions/hooks';
import { useSession } from '../../core/stores/session';
import { rpc, useToasts } from '../../core/net/reducers';
import { playSfx } from '../../core/audio';
import { getBridge } from '../../core/encounter/session';
import { upgradeSpec } from '../../core/items/catalogue';
import { describeUpgrade, fitsLine, ranksOf } from '../../core/items/upgrades';
import { ItemIcon } from './ItemCard';
import type { GearItem, HeroGear } from './useHeroGear';

/** The hero's pieces a gift can go on, with the rank it would reach on each (the rules' own `upgrade_options`). */
export function giftTargets(items: readonly GearItem[], upgradeId: number): { it: GearItem; rank: number }[] {
  const b = getBridge();
  if (!b) return [];
  const out: { it: GearItem; rank: number }[] = [];
  for (const it of items) {
    try {
      const o = b.upgradeOptions(it.defId, it.upgrades).options.find((x) => x.id === upgradeId);
      if (o && !o.why) out.push({ it, rank: o.rank });
    } catch { /* an unknown def */ }
  }
  return out;
}

export function UpgradeGifts({ g, selected, onPick }: { g: HeroGear; selected?: GearItem; onPick(id: bigint): void }) {
  const worldId = useSession((s) => s.worldId);
  const gifts = useUpgradeGifts(worldId).filter((x) => x.heroId === g.hero?.id);
  if (!gifts.length) return null;
  return (
    <section className="fk-gifts" aria-label="Gifts of enchantment" data-testid="upgrade-gifts">
      {gifts.map((gift) => <Gift key={gift.id.toString()} id={gift.id} upgradeId={gift.upgrade} giver={gift.giver} g={g} selected={selected} onPick={onPick} />)}
    </section>
  );
}

function Gift({ id, upgradeId, giver, g, selected, onPick }: { id: bigint; upgradeId: number; giver: string; g: HeroGear; selected?: GearItem; onPick(id: bigint): void }) {
  const push = useToasts((s) => s.push);
  const [busy, setBusy] = useState(false);
  const up = upgradeSpec(upgradeId);
  const targets = useMemo(() => giftTargets(g.items, upgradeId), [g.items, upgradeId]);
  if (!up) return null;
  const onSelected = selected && targets.find((t) => t.it.id === selected.id);
  const words = describeUpgrade(up, onSelected?.rank ?? 1);
  const style = up.look.glow ? ({ '--fk-glow': up.look.glow } as CSSProperties) : undefined;
  const lay = async (it: GearItem) => {
    setBusy(true);
    const r = await rpc.acceptUpgradeGift(id, it.id);
    setBusy(false);
    if (r.ok) {
      playSfx('item.enchant');
      const now = ranksOf([...it.upgrades, upgradeId]).find((x) => x.up.id === upgradeId);
      push(`${giver ? `${giver[0]!.toUpperCase()}${giver.slice(1)}` : 'The wonder'} lays ${up.name}${now && now.rank > 1 ? ` (now rank ${now.rank})` : ''} on the ${it.name}.`, 'success');
    }
  };
  const decline = async () => {
    const ok = await confirmDialog({ title: `Turn down ${up.name}?`, text: 'The gift is gone for good once it is refused.', confirmLabel: 'Turn it down', danger: true });
    if (!ok) return;
    const r = await rpc.declineUpgradeGift(id);
    if (r.ok) playSfx('ui.back');
  };
  return (
    <article className="fk-gift" style={style} data-testid="upgrade-gift" data-upgrade={upgradeId}>
      <span className="fk-gift__spark" aria-hidden>✦</span>
      <div className="fk-gift__body">
        <div className="fk-gift__head"><span className="fk-item-body__tag">A gift of enchantment</span>{giver && <span className="fk-faint">from {giver}</span>}</div>
        <b className="fk-gift__name">{words.name}</b>
        <span className="fk-gift__lines">{[...words.lines, ...(words.ability ? [`${words.ability.name}: ${words.ability.effects.join('; ')}`] : [])].join(' · ')}</span>
        <span className="fk-faint fk-gift__fits">{fitsLine(up)}. Choose a piece to lay it on, for nothing.</span>
        {targets.length ? (
          <ul className="fk-gift__targets" aria-label="Pieces it can go on">
            {targets.map(({ it, rank }) => (
              <li key={it.id.toString()}>
                <button type="button" className={`fk-gift__target ${selected?.id === it.id ? 'is-selected' : ''}`} onClick={() => onPick(it.id)} title={`${it.name}${rank > 1 ? ` (to rank ${rank})` : ''}`} data-testid="upgrade-gift-target" data-item-id={it.id.toString()}>
                  <ItemIcon def={it.defId} tier={it.tier} name={it.name} relic={it.relic} scale={1} framed upgrades={it.upgrades} />
                </button>
              </li>
            ))}
          </ul>
        ) : <span className="fk-faint">Nothing this hero carries can take it yet.</span>}
      </div>
      <div className="fk-gift__actions">
        <Button small variant="primary" disabled={busy || !onSelected} onClick={() => selected && void lay(selected)} title={onSelected ? `Lay it on the ${selected!.name}` : 'Select a piece it fits first'} data-testid="upgrade-gift-accept">
          {onSelected ? `Lay it on the ${selected!.name}` : 'Select a piece'}
        </Button>
        <Button small variant="ghost" disabled={busy} onClick={() => void decline()} data-testid="upgrade-gift-decline">Turn down</Button>
      </div>
    </article>
  );
}
